export const detectEnvironment = () => {
  const userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  const isAndroid = /android/i.test(userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  const isChrome = /chrome/i.test(userAgent);

  // Detect PWA mode
  const isInStandaloneMode =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true ||
    window.matchMedia("(display-mode: fullscreen)").matches;

  // Detect if it's a mobile browser
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    );

  return {
    isIOS,
    isAndroid,
    isSafari,
    isChrome,
    isInStandaloneMode,
    isMobile,
  };
};

export const downloadPdfFile = (pdfData: any, filename = "dasha.pdf") => {
  let blob;

  // Handle different PDF data formats
  if (pdfData instanceof Blob) {
    // Check if blob type is text/html and convert to PDF
    if (pdfData.type === "text/html") {
      // Convert HTML blob to PDF-ready blob
      blob = new Blob([pdfData], { type: "application/pdf" });
    } else {
      blob = pdfData;
    }
  } else if (typeof pdfData === "string" && pdfData.startsWith("%PDF")) {
    const uint8Array = new TextEncoder().encode(pdfData);
    blob = new Blob([uint8Array], { type: "application/pdf" });
  } else if (
    typeof pdfData === "string" &&
    (pdfData.includes("<html") || pdfData.includes("<!DOCTYPE"))
  ) {
    // Handle HTML string that should be converted to PDF
    blob = new Blob([pdfData], { type: "application/pdf" });
  } else if (pdfData.pdf) {
    blob = new Blob([pdfData.pdf], { type: "application/pdf" });
  } else if (typeof pdfData === "string") {
    // Handle base64 PDF data
    try {
      const byteCharacters = atob(pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], { type: "application/pdf" });
    } catch (e) {
      console.error("Error processing PDF data:", e);
      return;
    }
  }

  if (!blob) {
    console.error("Unable to create PDF blob");
    return;
  }

  const env = detectEnvironment();

  // Handle iOS PWA - show instructions instead of failing silently
  if (env.isIOS && env.isInStandaloneMode) {
    const dataUrl = URL.createObjectURL(blob);

    // Show instructions via alert (or you can use your app's toast/notification system)
    alert("PDF will open in a new tab. Use the share button to save to Files.");

    // Open PDF directly
    window.open(dataUrl, "_blank");
    return;
  }

  // Handle mobile devices (iOS Safari, Android Chrome, etc.)
  if (env.isMobile) {
    const dataUrl = URL.createObjectURL(blob);

    if (env.isIOS) {
      // iOS Safari - open in new tab, user can then save
      window.open(dataUrl, "_blank");
    } else if (env.isAndroid) {
      // Android - try download, fallback to opening in new tab
      try {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(dataUrl);
        }, 1000);
      } catch (e) {
        // Fallback: open in new tab
        window.open(dataUrl, "_blank");
      }
    } else {
      // Other mobile browsers
      window.open(dataUrl, "_blank");
    }
    return;
  }

  // Desktop browsers
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    // IE/Edge legacy
    (window.navigator as any).msSaveOrOpenBlob(blob, filename);
  } else {
    // Modern browsers
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
  }
};

export const downloadImageFile = (imageData: any, filename = "dasha.jpg") => {
  let blob;

  // First priority: Handle blob objects (including image/png blobs)
  if (imageData instanceof Blob) {
    blob = imageData;

    // Update filename extension based on blob type
    if (blob.type === "image/png" && !filename.toLowerCase().endsWith(".png")) {
      filename = filename.replace(/\.(jpg|jpeg|gif|webp)$/i, ".png");
    } else if (
      blob.type === "image/jpeg" &&
      !filename.toLowerCase().endsWith(".jpg") &&
      !filename.toLowerCase().endsWith(".jpeg")
    ) {
      filename = filename.replace(/\.(png|gif|webp)$/i, ".jpg");
    } else if (
      blob.type === "image/webp" &&
      !filename.toLowerCase().endsWith(".webp")
    ) {
      filename = filename.replace(/\.(jpg|jpeg|png|gif)$/i, ".webp");
    } else if (
      blob.type === "image/gif" &&
      !filename.toLowerCase().endsWith(".gif")
    ) {
      filename = filename.replace(/\.(jpg|jpeg|png|webp)$/i, ".gif");
    }
  }
  // Handle base64 strings
  else if (
    typeof imageData === "string" &&
    (imageData.startsWith("/9j/") ||
      imageData.startsWith("iVBOR") ||
      imageData.startsWith("data:image/"))
  ) {
    let mimeType = "image/jpeg";
    let base64Data = imageData;

    // Handle data URLs
    if (imageData.startsWith("data:image/")) {
      const matches = imageData.match(/^data:image\/([^;]+);base64,(.*)$/);
      if (matches) {
        mimeType = `image/${matches[1]}`;
        base64Data = matches[2];
        filename = filename.replace(
          /\.(jpg|jpeg|png|gif|webp)$/i,
          `.${matches[1]}`
        );
      }
    } else {
      // Handle raw base64
      if (imageData.startsWith("iVBOR")) {
        mimeType = "image/png";
        filename = filename.replace(/\.(jpg|jpeg)$/i, ".png");
      }
    }

    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], { type: mimeType });
    } catch (e) {
      console.error("Error processing image data:", e);
      return;
    }
  }
  // Handle string data
  else if (typeof imageData === "string") {
    blob = new Blob([imageData], { type: "image/jpeg" });
  }
  // Handle objects with image property
  else if (imageData && imageData.image) {
    blob = new Blob([imageData.image], { type: "image/jpeg" });
  }

  if (!blob) {
    console.error("Unable to create image blob");
    return;
  }

  const env = detectEnvironment();

  // Handle mobile devices (iOS and Android)
  if (env.isMobile) {
    const dataUrl = URL.createObjectURL(blob);

    if (env.isIOS) {
      // iOS - open in new tab with save instructions
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>Image - ${filename}</title>
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
            </head>
            <body style="margin:0;padding:20px;text-align:center;background:#f0f0f0;font-family:-apple-system,BlinkMacSystemFont,sans-serif;">
              <h3 style="color:#333;">Long press the image below to save</h3>
              <img src="${dataUrl}" style="max-width:100%;height:auto;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);" alt="${filename}"/>
              <div style="margin-top:20px;padding:15px;background:white;border-radius:8px;text-align:left;">
                <h4 style="margin-top:0;color:#333;">How to save on iOS:</h4>
                <p style="margin:5px 0;color:#666;">1. Long press the image above</p>
                <p style="margin:5px 0;color:#666;">2. Select "Save to Photos" or "Save to Files"</p>
                <p style="margin:5px 0;color:#666;">3. Choose your preferred location</p>
              </div>
            </body>
          </html>
        `);
      }
    } else if (env.isAndroid) {
      // Android - try direct download first, fallback to instructions
      try {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(dataUrl);
        }, 1000);
      } catch (e) {
        // Fallback: show image in new tab with instructions
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head>
                <title>Image - ${filename}</title>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
              </head>
              <body style="margin:0;padding:20px;text-align:center;background:#f0f0f0;font-family:Roboto,sans-serif;">
                <h3 style="color:#333;">Long press the image below to save</h3>
                <img src="${dataUrl}" style="max-width:100%;height:auto;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);" alt="${filename}"/>
                <div style="margin-top:20px;padding:15px;background:white;border-radius:8px;text-align:left;">
                  <h4 style="margin-top:0;color:#333;">How to save on Android:</h4>
                  <p style="margin:5px 0;color:#666;">1. Long press the image above</p>
                  <p style="margin:5px 0;color:#666;">2. Select "Download image" or "Save image"</p>
                  <p style="margin:5px 0;color:#666;">3. Check your Downloads folder or Gallery</p>
                </div>
              </body>
            </html>
          `);
        }
      }
    }
    return;
  }

  // Desktop browsers (Windows, macOS, Linux)
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    // IE/Edge legacy support
    (window.navigator as any).msSaveOrOpenBlob(blob, filename);
  } else {
    // Modern browsers (Chrome, Firefox, Safari, Edge Chromium)
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";

    // Ensure the element is added to DOM before clicking
    document.body.appendChild(a);

    // Trigger download
    a.click();

    // Clean up
    setTimeout(() => {
      if (document.body.contains(a)) {
        document.body.removeChild(a);
      }
      URL.revokeObjectURL(url);
    }, 1000);
  }
};

export const downloadZipFile = (zipData: any, filename = "dasha.zip") => {
  let blob;

  // Handle different zip data formats
  if (zipData instanceof Blob) {
    blob = zipData;
  } else if (typeof zipData === "string") {
    // Handle base64 zip data
    try {
      const byteCharacters = atob(zipData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      blob = new Blob([byteArray], { type: "application/zip" });
    } catch (e) {
      console.error("Error processing zip data:", e);
      return;
    }
  } else if (zipData && zipData.zip) {
    blob = new Blob([zipData.zip], { type: "application/zip" });
  }

  if (!blob) {
    console.error("Unable to create zip blob");
    return;
  }

  const env = detectEnvironment();

  // Handle mobile devices
  if (env.isMobile) {
    const dataUrl = URL.createObjectURL(blob);

    if (env.isIOS) {
      // iOS Safari - open in new tab, user can then save
      window.open(dataUrl, "_blank");
    } else if (env.isAndroid) {
      // Android - try download
      try {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(dataUrl);
        }, 1000);
      } catch (e) {
        // Fallback: open in new tab
        window.open(dataUrl, "_blank");
      }
    } else {
      // Other mobile browsers
      window.open(dataUrl, "_blank");
    }
    return;
  }

  // Desktop browsers
  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
    // IE/Edge legacy
    (window.navigator as any).msSaveOrOpenBlob(blob, filename);
  } else {
    // Modern browsers
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
  }
};
